import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../context/ThemeContext';

// Icons
const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CalendarOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="4" y1="4" x2="20" y2="20" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const SaveIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const ChevronDownIcon = ({ style }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const DAYS_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Tier limits for weekly events
const TIER_EVENT_LIMITS = {
  0: 0,  // Free
  1: 2,  // Basic
  2: 2,  // Enhanced
  3: 4,  // Premium
};

// Tier limits for special events (per year)
const TIER_SPECIAL_EVENT_LIMITS = {
  0: 0,   // Free
  1: 0,   // Basic
  2: 4,   // Enhanced - 4/year
  3: 10,  // Premium - 10/year
};

const MAX_EVENT_SLOTS = 4;

const TIER_NAMES = {
  0: { name: 'Listed', color: '#6b7280', desc: 'Basic listing on the map' },
  1: { name: 'Basic Partner', color: '#0ea5e9', desc: '2 weekly events, shop page' },
  2: { name: 'Verified Partner', color: '#8b5cf6', desc: '2 weekly events, 4 special events/year, photo gallery' },
  3: { name: 'Premium Partner', color: '#f59e0b', desc: '4 weekly events, 10 special events/year, priority listing' },
};

// Event types
const EVENT_TYPES = [
  { id: 'trade', label: 'Trade' },
  { id: 'tournament', label: 'Tournament' },
  { id: 'card_show', label: 'Card Show' },
];

// Determine Day vs Night based on start time (5PM+ = Night)
const getDayOrNight = (startTime) => {
  if (!startTime) return 'Night';
  const [hours] = startTime.split(':');
  const hour = parseInt(hours);
  return hour >= 17 ? 'Night' : 'Day'; // 17 = 5PM
};

// Generate event display name
const generateEventName = (eventType, dayOfWeek, startTime) => {
  const typeLabel = EVENT_TYPES.find(t => t.id === eventType)?.label || 'Event';
  const dayNight = getDayOrNight(startTime);
  return `${typeLabel} ${dayNight}`;
};

// Generate event subtitle
const generateEventSubtitle = (dayOfWeek, startTime, endTime) => {
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    let h = parseInt(hours);
    const period = h >= 12 ? 'PM' : 'AM';
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    return `${h}:${minutes} ${period}`;
  };
  return `${dayOfWeek}s, ${formatTime(startTime)} - ${formatTime(endTime)}`;
};

// Time options for dropdowns
const TIME_OPTIONS = [
  '6:00 AM', '6:30 AM', '7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM',
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
  '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM',
  '9:00 PM', '9:30 PM', '10:00 PM', '10:30 PM', '11:00 PM', '11:30 PM',
];

// Parse "9:00 AM - 5:00 PM" into { open: "9:00 AM", close: "5:00 PM" }
const parseHoursString = (str) => {
  if (!str || str.toLowerCase() === 'closed') {
    return { closed: true, open: '', close: '' };
  }
  const parts = str.split(' - ');
  if (parts.length === 2) {
    return { closed: false, open: parts[0].trim(), close: parts[1].trim() };
  }
  return { closed: false, open: '', close: '' };
};

// Convert { open: "9:00 AM", close: "5:00 PM" } to "9:00 AM - 5:00 PM"
const formatHoursString = (dayData) => {
  if (dayData.closed) return 'Closed';
  if (dayData.open && dayData.close) {
    return `${dayData.open} - ${dayData.close}`;
  }
  return '';
};

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const FileTextIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const CameraIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const LocationMyShop = ({ location }) => {
  const { theme } = useTheme();

  // Shop details state
  const [description, setDescription] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [detailsSaving, setDetailsSaving] = useState(false);

  // Tier state
  const [currentTier, setCurrentTier] = useState(location?.subscription_tier || 0);
  const [tierSaving, setTierSaving] = useState(false);

  // Photo state
  const [heroPhoto, setHeroPhoto] = useState('');
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);

  // Hours state
  const [hours, setHours] = useState({});
  const [editHours, setEditHours] = useState({}); // Temporary state for editing
  const [hoursSaving, setHoursSaving] = useState(false);
  const [isEditingHours, setIsEditingHours] = useState(false);
  const [hoursCollapsed, setHoursCollapsed] = useState(true);

  // Blocked times state
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [blockedLoading, setBlockedLoading] = useState(false);
  const [showAddBlocked, setShowAddBlocked] = useState(false);
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [newBlockedReason, setNewBlockedReason] = useState('');

  // Weekly events state
  const [weeklyEvents, setWeeklyEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEventType, setNewEventType] = useState('');
  const [newEventDay, setNewEventDay] = useState('');
  const [newEventStart, setNewEventStart] = useState('');
  const [newEventEnd, setNewEventEnd] = useState('');
  const [eventSaving, setEventSaving] = useState(false);

  // Special events state (one-time events)
  const [specialEvents, setSpecialEvents] = useState([]);
  const [specialEventsLoading, setSpecialEventsLoading] = useState(false);
  const [showAddSpecial, setShowAddSpecial] = useState(false);
  const [newSpecialName, setNewSpecialName] = useState('');
  const [newSpecialDate, setNewSpecialDate] = useState('');
  const [newSpecialStart, setNewSpecialStart] = useState('');
  const [newSpecialEnd, setNewSpecialEnd] = useState('');
  const [newSpecialDesc, setNewSpecialDesc] = useState('');
  const [specialSaving, setSpecialSaving] = useState(false);
  const [specialEventsThisYear, setSpecialEventsThisYear] = useState(0);

  // Get tier limits
  const subscriptionTier = currentTier;
  const eventLimit = TIER_EVENT_LIMITS[subscriptionTier] || 0;
  const specialEventLimit = TIER_SPECIAL_EVENT_LIMITS[subscriptionTier] || 0;
  const canAddMoreEvents = weeklyEvents.length < eventLimit;
  const canAddMoreSpecialEvents = specialEventsThisYear < specialEventLimit;
  const activeEvents = weeklyEvents.slice(0, eventLimit);
  const inactiveEvents = weeklyEvents.slice(eventLimit);

  // Parse existing hours on mount
  useEffect(() => {
    if (location?.operating_hours) {
      try {
        let parsed = location.operating_hours;
        // Handle string (may be double-stringified from Supabase)
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
          // Check if still a string (double-stringified)
          if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
          }
        }
        setHours(parsed);
      } catch (err) {
        console.error('Error parsing operating_hours:', err);
        setHours({});
      }
    }
  }, [location?.operating_hours]);

  // Load shop details on mount
  useEffect(() => {
    if (location) {
      setDescription(location.description || '');
      setPhone(location.phone || '');
      setWebsite(location.website || '');
      setHeroPhoto(location.photo_url || '');
      setGalleryPhotos(location.photos || []);
    }
  }, [location?.id]);

  // Save shop details (description, phone, website)
  const saveDetails = async () => {
    if (!location?.id) return;
    setDetailsSaving(true);
    try {
      const { error } = await supabase
        .from('locations')
        .update({
          description: description.trim(),
          phone: phone.trim(),
          website: website.trim(),
        })
        .eq('id', location.id);
      if (error) throw error;
      setIsEditingDetails(false);
    } catch (err) {
      console.error('Error saving details:', err);
      alert('Error saving. Please try again.');
    } finally {
      setDetailsSaving(false);
    }
  };

  // Upload a photo to location-photos bucket
  const uploadPhoto = async (file, isHero = false) => {
    if (!location?.id || !file) return;
    setPhotoUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const filename = `${location.id}/${isHero ? 'hero' : Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('location-photos')
        .upload(filename, file, { contentType: file.type, upsert: true });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('location-photos')
        .getPublicUrl(filename);
      const publicUrl = urlData.publicUrl;

      if (isHero) {
        await supabase.from('locations').update({ photo_url: publicUrl }).eq('id', location.id);
        setHeroPhoto(publicUrl);
      } else {
        const updated = [...galleryPhotos, publicUrl];
        await supabase.from('locations').update({ photos: updated }).eq('id', location.id);
        setGalleryPhotos(updated);
      }
    } catch (err) {
      console.error('Error uploading photo:', err);
      alert('Error uploading photo. Please try again.');
    } finally {
      setPhotoUploading(false);
    }
  };

  // Remove a gallery photo
  const removeGalleryPhoto = async (url) => {
    if (!location?.id) return;
    try {
      const updated = galleryPhotos.filter(p => p !== url);
      await supabase.from('locations').update({ photos: updated }).eq('id', location.id);
      setGalleryPhotos(updated);
    } catch (err) {
      console.error('Error removing photo:', err);
    }
  };

  // Remove hero photo
  const removeHeroPhoto = async () => {
    if (!location?.id) return;
    try {
      await supabase.from('locations').update({ photo_url: null }).eq('id', location.id);
      setHeroPhoto('');
    } catch (err) {
      console.error('Error removing hero photo:', err);
    }
  };

  // Sync tier from location prop
  useEffect(() => {
    if (location?.subscription_tier !== undefined) {
      setCurrentTier(location.subscription_tier);
    }
  }, [location?.subscription_tier]);

  // Change subscription tier
  const changeTier = async (newTier) => {
    if (!location?.id || newTier === currentTier) return;
    setTierSaving(true);
    try {
      const { error } = await supabase
        .from('locations')
        .update({ subscription_tier: newTier })
        .eq('id', location.id);
      if (error) throw error;
      setCurrentTier(newTier);
    } catch (err) {
      console.error('Error changing tier:', err);
      alert('Error changing plan. Please try again.');
    } finally {
      setTierSaving(false);
    }
  };

  // Fetch blocked times, weekly events, and special events
  useEffect(() => {
    if (location?.id) {
      fetchBlockedTimes();
      fetchWeeklyEvents();
      fetchSpecialEvents();
    }
  }, [location?.id]);

  // Start editing hours - copy current hours to edit state
  const startEditingHours = () => {
    const parsed = {};
    DAYS_ORDER.forEach(day => {
      parsed[day] = parseHoursString(hours[day] || '');
    });
    setEditHours(parsed);
    setIsEditingHours(true);
  };

  // Cancel editing
  const cancelEditingHours = () => {
    setEditHours({});
    setIsEditingHours(false);
  };

  // Update edit state for a specific day
  const handleEditHoursChange = (day, field, value) => {
    setEditHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  // Toggle closed status for a day
  const toggleDayClosed = (day) => {
    setEditHours(prev => ({
      ...prev,
      [day]: { ...prev[day], closed: !prev[day]?.closed, open: '', close: '' },
    }));
  };

  const fetchBlockedTimes = async () => {
    if (!location?.id) return;

    setBlockedLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('location_blocked_times')
        .select('*')
        .eq('location_id', location.id)
        .gte('date', today)
        .order('date', { ascending: true });

      if (!error) {
        setBlockedTimes(data || []);
      }
    } catch (err) {
      console.error('Error fetching blocked times:', err);
    } finally {
      setBlockedLoading(false);
    }
  };

  const saveHours = async () => {
    if (!location?.id) return;

    // Convert editHours back to the Supabase format
    const formattedHours = {};
    DAYS_ORDER.forEach(day => {
      formattedHours[day] = formatHoursString(editHours[day] || { closed: true });
    });

    setHoursSaving(true);
    try {
      const { error } = await supabase
        .from('locations')
        .update({ operating_hours: formattedHours })
        .eq('id', location.id);

      if (error) throw error;
      setHours(formattedHours);
      setIsEditingHours(false);
      setEditHours({});
    } catch (err) {
      console.error('Error saving hours:', err);
      alert('Error saving hours. Please try again.');
    } finally {
      setHoursSaving(false);
    }
  };

  const addBlockedDate = async () => {
    if (!location?.id || !newBlockedDate) return;

    try {
      const { error } = await supabase
        .from('location_blocked_times')
        .insert({
          location_id: location.id,
          date: newBlockedDate,
          reason: newBlockedReason || null,
        });

      if (error) throw error;

      setNewBlockedDate('');
      setNewBlockedReason('');
      setShowAddBlocked(false);
      fetchBlockedTimes();
    } catch (err) {
      console.error('Error adding blocked date:', err);
      alert('Error adding blocked date.');
    }
  };

  const removeBlockedDate = async (id) => {
    try {
      const { error } = await supabase
        .from('location_blocked_times')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchBlockedTimes();
    } catch (err) {
      console.error('Error removing blocked date:', err);
      alert('Error removing blocked date.');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Weekly Events functions
  const fetchWeeklyEvents = async () => {
    if (!location?.id) return;
    setEventsLoading(true);
    try {
      const { data, error } = await supabase
        .from('location_events')
        .select('*')
        .eq('location_id', location.id)
        .eq('is_recurring', true)
        .order('recurrence_day');

      if (error) throw error;
      setWeeklyEvents(data || []);
    } catch (err) {
      console.error('Error fetching weekly events:', err);
    } finally {
      setEventsLoading(false);
    }
  };

  const addWeeklyEvent = async () => {
    if (!location?.id || !newEventType || !newEventDay || !newEventStart || !newEventEnd) {
      alert('Please fill in all required fields');
      return;
    }

    if (!canAddMoreEvents) {
      alert(`You've reached the limit of ${eventLimit} weekly events for your tier.`);
      return;
    }

    setEventSaving(true);
    try {
      // Convert time string to proper time format
      const formatTime = (timeStr) => {
        const [time, period] = timeStr.split(' ');
        let [hours, minutes] = time.split(':');
        hours = parseInt(hours);
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        return `${hours.toString().padStart(2, '0')}:${minutes}:00`;
      };

      const startTimeFormatted = formatTime(newEventStart);
      const eventName = generateEventName(newEventType, newEventDay, startTimeFormatted);

      const { error } = await supabase
        .from('location_events')
        .insert({
          location_id: location.id,
          name: eventName,
          description: newEventType, // Store event type in description for reference
          event_date: new Date().toISOString().split('T')[0],
          start_time: startTimeFormatted,
          end_time: formatTime(newEventEnd),
          is_recurring: true,
          recurrence_day: newEventDay,
        });

      if (error) throw error;

      setNewEventType('');
      setNewEventDay('');
      setNewEventStart('');
      setNewEventEnd('');
      setShowAddEvent(false);
      fetchWeeklyEvents();
    } catch (err) {
      console.error('Error adding weekly event:', err);
      alert('Error adding weekly event.');
    } finally {
      setEventSaving(false);
    }
  };

  const removeWeeklyEvent = async (id) => {
    if (!window.confirm('Remove this weekly event?')) return;
    try {
      const { error } = await supabase
        .from('location_events')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchWeeklyEvents();
    } catch (err) {
      console.error('Error removing weekly event:', err);
      alert('Error removing weekly event.');
    }
  };

  // Special Events functions
  const fetchSpecialEvents = async () => {
    if (!location?.id) return;
    setSpecialEventsLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const currentYear = new Date().getFullYear();
      const yearStart = `${currentYear}-01-01`;

      // Fetch upcoming special events
      const { data, error } = await supabase
        .from('location_events')
        .select('*')
        .eq('location_id', location.id)
        .eq('is_recurring', false)
        .gte('event_date', today)
        .order('event_date', { ascending: true });

      if (error) throw error;
      setSpecialEvents(data || []);

      // Count events created this year for quota
      const { count, error: countError } = await supabase
        .from('location_events')
        .select('*', { count: 'exact', head: true })
        .eq('location_id', location.id)
        .eq('is_recurring', false)
        .gte('event_date', yearStart);

      if (!countError) {
        setSpecialEventsThisYear(count || 0);
      }
    } catch (err) {
      console.error('Error fetching special events:', err);
    } finally {
      setSpecialEventsLoading(false);
    }
  };

  const addSpecialEvent = async () => {
    if (!location?.id || !newSpecialName || !newSpecialDate || !newSpecialStart || !newSpecialEnd) {
      alert('Please fill in all required fields');
      return;
    }

    if (!canAddMoreSpecialEvents) {
      alert(`You've used all ${specialEventLimit} special events for this year.`);
      return;
    }

    setSpecialSaving(true);
    try {
      const formatTime = (timeStr) => {
        const [time, period] = timeStr.split(' ');
        let [hours, minutes] = time.split(':');
        hours = parseInt(hours);
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        return `${hours.toString().padStart(2, '0')}:${minutes}:00`;
      };

      const { error } = await supabase
        .from('location_events')
        .insert({
          location_id: location.id,
          name: newSpecialName,
          description: newSpecialDesc || null,
          event_date: newSpecialDate,
          start_time: formatTime(newSpecialStart),
          end_time: formatTime(newSpecialEnd),
          is_recurring: false,
          notify_followers: true, // Special events notify followers
        });

      if (error) throw error;

      setNewSpecialName('');
      setNewSpecialDate('');
      setNewSpecialStart('');
      setNewSpecialEnd('');
      setNewSpecialDesc('');
      setShowAddSpecial(false);
      fetchSpecialEvents();
    } catch (err) {
      console.error('Error adding special event:', err);
      alert('Error adding special event.');
    } finally {
      setSpecialSaving(false);
    }
  };

  const removeSpecialEvent = async (id) => {
    if (!window.confirm('Remove this special event?')) return;
    try {
      const { error } = await supabase
        .from('location_events')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchSpecialEvents();
    } catch (err) {
      console.error('Error removing special event:', err);
      alert('Error removing special event.');
    }
  };

  // Format date for display
  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Format time from DB format to display format
  const formatEventTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    let h = parseInt(hours);
    const period = h >= 12 ? 'PM' : 'AM';
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    return `${h}:${minutes} ${period}`;
  };

  // Get min date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <div style={styles.container}>
      {/* Location Info - Read Only */}
      <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <div style={styles.sectionHeader}>
          <MapPinIcon />
          <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Location Info</h3>
        </div>

        <div style={{ ...styles.readOnlyCard, backgroundColor: theme.bg, borderColor: theme.border }}>
          <div style={styles.infoRow}>
            <span style={{ ...styles.infoLabel, color: theme.textSecondary }}>Store Name</span>
            <span style={{ ...styles.infoValue, color: theme.text }}>{location?.store_name || '-'}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={{ ...styles.infoLabel, color: theme.textSecondary }}>Address</span>
            <span style={{ ...styles.infoValue, color: theme.text }}>{location?.address || '-'}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={{ ...styles.infoLabel, color: theme.textSecondary }}>City, State</span>
            <span style={{ ...styles.infoValue, color: theme.text }}>
              {location?.city}, {location?.state}
            </span>
          </div>
        </div>

        <div style={{ ...styles.contactNote, backgroundColor: 'rgba(139, 92, 246, 0.1)', borderColor: 'rgba(139, 92, 246, 0.3)' }}>
          <MailIcon />
          <div style={styles.contactNoteText}>
            <span style={{ ...styles.contactNoteTitle, color: '#8b5cf6' }}>Need to update your location?</span>
            <span style={{ ...styles.contactNoteDesc, color: theme.textSecondary }}>
              Contact us at tradersupport@cardchase.org to request changes to your store name or address.
            </span>
          </div>
        </div>
      </div>

      {/* Subscription Tier */}
      <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <div style={styles.sectionHeader}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Your Plan</h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[0, 1, 2, 3].map(tier => {
            const info = TIER_NAMES[tier];
            const isActive = currentTier === tier;
            return (
              <button
                key={tier}
                onClick={() => changeTier(tier)}
                disabled={tierSaving}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 10,
                  border: isActive ? `2px solid ${info.color}` : `1px solid ${theme.border}`,
                  backgroundColor: isActive ? `${info.color}15` : theme.bg,
                  cursor: tierSaving ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  opacity: tierSaving ? 0.5 : 1,
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: '50%',
                  border: isActive ? `5px solid ${info.color}` : `2px solid ${theme.border}`,
                  flexShrink: 0,
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: isActive ? info.color : theme.text }}>
                    {info.name}
                  </div>
                  <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 2 }}>
                    {info.desc}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Shop Details - Description, Phone, Website */}
      <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <div style={styles.sectionHeader}>
          <FileTextIcon />
          <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Shop Details</h3>
          {!isEditingDetails ? (
            <button
              style={{ ...styles.editButton }}
              onClick={() => setIsEditingDetails(true)}
            >
              <EditIcon />
              <span>Edit</span>
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.cancelButton} onClick={() => {
                setDescription(location?.description || '');
                setPhone(location?.phone || '');
                setWebsite(location?.website || '');
                setIsEditingDetails(false);
              }}>Cancel</button>
              <button
                style={{ ...styles.saveButton, opacity: detailsSaving ? 0.5 : 1 }}
                onClick={saveDetails}
                disabled={detailsSaving}
              >
                <SaveIcon />
                <span>{detailsSaving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          )}
        </div>

        {isEditingDetails ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={styles.formGroup}>
              <label style={{ ...styles.formLabel, color: theme.text }}>About / Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Tell visitors about your shop..."
                rows={4}
                style={{
                  ...styles.formInput,
                  borderColor: theme.border,
                  backgroundColor: theme.bg,
                  color: theme.text,
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={{ ...styles.formLabel, color: theme.text }}>Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                style={{
                  ...styles.formInput,
                  borderColor: theme.border,
                  backgroundColor: theme.bg,
                  color: theme.text,
                }}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={{ ...styles.formLabel, color: theme.text }}>Website</label>
              <input
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://yourshop.com"
                style={{
                  ...styles.formInput,
                  borderColor: theme.border,
                  backgroundColor: theme.bg,
                  color: theme.text,
                }}
              />
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ ...styles.readOnlyCard, backgroundColor: theme.bg, borderColor: theme.border }}>
              <div style={styles.infoRow}>
                <span style={{ ...styles.infoLabel, color: theme.textSecondary }}>About</span>
                <span style={{ ...styles.infoValue, color: theme.text, whiteSpace: 'pre-wrap' }}>
                  {description || <span style={{ color: theme.textMuted, fontStyle: 'italic' }}>No description yet</span>}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={{ ...styles.infoLabel, color: theme.textSecondary }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><PhoneIcon /> Phone</span>
                </span>
                <span style={{ ...styles.infoValue, color: theme.text }}>
                  {phone || <span style={{ color: theme.textMuted, fontStyle: 'italic' }}>Not set</span>}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={{ ...styles.infoLabel, color: theme.textSecondary }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><GlobeIcon /> Website</span>
                </span>
                <span style={{ ...styles.infoValue, color: theme.text }}>
                  {website || <span style={{ color: theme.textMuted, fontStyle: 'italic' }}>Not set</span>}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Photos */}
      <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <div style={styles.sectionHeader}>
          <CameraIcon />
          <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Photos</h3>
        </div>

        {/* Hero Photo */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ ...styles.formLabel, color: theme.text, display: 'block', marginBottom: 8 }}>
            Main Photo
          </label>
          {heroPhoto ? (
            <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', maxHeight: 200 }}>
              <img
                src={heroPhoto}
                alt="Shop hero"
                style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
              />
              <button
                onClick={removeHeroPhoto}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  backgroundColor: 'rgba(239, 68, 68, 0.9)', color: '#fff',
                  border: 'none', borderRadius: 6, padding: '6px 10px',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              height: 120, borderRadius: 10, border: `2px dashed ${theme.border}`,
              backgroundColor: theme.bg, cursor: 'pointer', gap: 8,
            }}>
              <CameraIcon />
              <span style={{ fontSize: 13, color: theme.textSecondary }}>
                {photoUploading ? 'Uploading...' : 'Tap to upload main photo'}
              </span>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0], true)}
                disabled={photoUploading}
              />
            </label>
          )}
        </div>

        {/* Gallery Photos */}
        <div>
          <label style={{ ...styles.formLabel, color: theme.text, display: 'block', marginBottom: 8 }}>
            Gallery ({galleryPhotos.length} photos)
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {galleryPhotos.map((url, i) => (
              <div key={i} style={{ position: 'relative', width: 90, height: 90, borderRadius: 8, overflow: 'hidden' }}>
                <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button
                  onClick={() => removeGalleryPhoto(url)}
                  style={{
                    position: 'absolute', top: 2, right: 2,
                    backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff',
                    border: 'none', borderRadius: 4, padding: '2px 5px',
                    fontSize: 10, cursor: 'pointer', lineHeight: 1,
                  }}
                >
                  X
                </button>
              </div>
            ))}
            <label style={{
              width: 90, height: 90, borderRadius: 8, border: `2px dashed ${theme.border}`,
              backgroundColor: theme.bg, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: 4,
            }}>
              <PlusIcon />
              <span style={{ fontSize: 10, color: theme.textSecondary }}>
                {photoUploading ? '...' : 'Add'}
              </span>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0], false)}
                disabled={photoUploading}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Blocked Dates */}
      <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <div style={styles.sectionHeader}>
          <CalendarOffIcon />
          <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Blocked Dates</h3>
          <button
            style={{ ...styles.addButton, backgroundColor: '#8b5cf6' }}
            onClick={() => setShowAddBlocked(true)}
          >
            <PlusIcon />
            <span>Add</span>
          </button>
        </div>

        <p style={{ ...styles.blockedDesc, color: theme.textSecondary }}>
          Block specific dates when your shop is unavailable for trades (holidays, private events, closures).
        </p>

        {blockedLoading ? (
          <div style={styles.loadingState}>
            <div style={{ ...styles.spinner, borderTopColor: '#8b5cf6' }} />
          </div>
        ) : blockedTimes.length === 0 ? (
          <div style={{ ...styles.emptyState, backgroundColor: theme.bg, borderColor: theme.border }}>
            <span style={{ color: theme.textMuted }}>No blocked dates scheduled</span>
          </div>
        ) : (
          <div style={styles.blockedList}>
            {blockedTimes.map(bt => (
              <div key={bt.id} style={{ ...styles.blockedCard, backgroundColor: theme.bg, borderColor: theme.border }}>
                <div style={styles.blockedInfo}>
                  <span style={{ ...styles.blockedDate, color: theme.text }}>{formatDate(bt.date)}</span>
                  {bt.reason && (
                    <span style={{ ...styles.blockedReason, color: theme.textSecondary }}>{bt.reason}</span>
                  )}
                </div>
                <button
                  style={styles.removeButton}
                  onClick={() => removeBlockedDate(bt.id)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Weekly Events - Slot Based System */}
      <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <div style={styles.sectionHeader}>
          <CalendarIcon />
          <div style={styles.sectionTitleWrap}>
            <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Weekly Events</h3>
            <span style={{ ...styles.tierLimit, color: theme.textMuted }}>
              {Math.min(weeklyEvents.length, eventLimit)} / {eventLimit} active
            </span>
          </div>
          {canAddMoreEvents && eventLimit > 0 && (
            <button
              style={{ ...styles.addButton, backgroundColor: '#8b5cf6' }}
              onClick={() => setShowAddEvent(true)}
            >
              <PlusIcon />
              <span>Add</span>
            </button>
          )}
        </div>

        {eventsLoading ? (
          <div style={styles.loadingState}>
            <div style={{ ...styles.spinner, borderTopColor: '#8b5cf6' }} />
          </div>
        ) : (
          <div style={styles.eventSlots}>
            {/* Active Events (within tier limit) */}
            {activeEvents.map((event, idx) => (
              <div key={event.id} style={{ ...styles.eventSlot, backgroundColor: theme.bg, borderColor: theme.border }}>
                <div style={styles.slotNumber}>
                  <span style={{ ...styles.slotBadge, backgroundColor: '#22c55e', color: '#fff' }}>{idx + 1}</span>
                </div>
                <div style={styles.eventInfo}>
                  <span style={{ ...styles.eventName, color: theme.text }}>{event.name}</span>
                  <span style={{ ...styles.eventTime, color: theme.textSecondary }}>
                    {generateEventSubtitle(event.recurrence_day, event.start_time, event.end_time)}
                  </span>
                </div>
                <button style={styles.removeButton} onClick={() => removeWeeklyEvent(event.id)}>
                  <TrashIcon />
                </button>
              </div>
            ))}

            {/* Empty Active Slots (can add more) */}
            {Array.from({ length: Math.max(0, eventLimit - weeklyEvents.length) }).map((_, idx) => (
              <div
                key={`empty-${idx}`}
                style={{ ...styles.eventSlot, ...styles.emptySlot, backgroundColor: theme.bg, borderColor: theme.border, cursor: 'pointer' }}
                onClick={() => setShowAddEvent(true)}
              >
                <div style={styles.slotNumber}>
                  <span style={{ ...styles.slotBadge, backgroundColor: 'rgba(139, 92, 246, 0.2)', color: '#8b5cf6' }}>
                    {activeEvents.length + idx + 1}
                  </span>
                </div>
                <div style={styles.eventInfo}>
                  <span style={{ ...styles.emptySlotText, color: theme.textMuted }}>+ Add weekly event</span>
                </div>
              </div>
            ))}

            {/* Inactive Events (over tier limit - downgraded) */}
            {inactiveEvents.length > 0 && (
              <>
                <div style={styles.separatorLine}>
                  <span style={{ ...styles.separatorText, color: '#ef4444', backgroundColor: theme.bgSheet }}>
                    Not showing on CardChase
                  </span>
                </div>
                {inactiveEvents.map((event, idx) => (
                  <div key={event.id} style={{ ...styles.eventSlot, ...styles.inactiveSlot, backgroundColor: theme.bg, borderColor: theme.border }}>
                    <div style={styles.slotNumber}>
                      <span style={{ ...styles.slotBadge, backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}>
                        {eventLimit + idx + 1}
                      </span>
                    </div>
                    <div style={styles.eventInfo}>
                      <span style={{ ...styles.eventName, color: theme.textMuted }}>{event.name}</span>
                      <span style={{ ...styles.eventTime, color: theme.textMuted }}>
                        {generateEventSubtitle(event.recurrence_day, event.start_time, event.end_time)}
                      </span>
                    </div>
                    <button style={{ ...styles.removeButton, opacity: 0.5 }} onClick={() => removeWeeklyEvent(event.id)}>
                      <TrashIcon />
                    </button>
                  </div>
                ))}
              </>
            )}

            {/* Locked Slots (upgrade to unlock) */}
            {Array.from({ length: Math.max(0, MAX_EVENT_SLOTS - Math.max(eventLimit, weeklyEvents.length)) }).map((_, idx) => (
              <div
                key={`locked-${idx}`}
                style={{ ...styles.eventSlot, ...styles.lockedSlot, backgroundColor: theme.bg, borderColor: theme.border }}
              >
                <div style={styles.slotNumber}>
                  <span style={{ ...styles.slotBadge, backgroundColor: 'rgba(107, 114, 128, 0.2)', color: '#9ca3af' }}>
                    {Math.max(eventLimit, weeklyEvents.length) + idx + 1}
                  </span>
                </div>
                <div style={styles.eventInfo}>
                  <span style={{ ...styles.lockedSlotText, color: theme.textMuted }}>Upgrade to unlock</span>
                </div>
              </div>
            ))}

            {/* No events and no slots message */}
            {eventLimit === 0 && weeklyEvents.length === 0 && (
              <div style={{ ...styles.upgradeBox, backgroundColor: 'rgba(139, 92, 246, 0.1)', borderColor: 'rgba(139, 92, 246, 0.3)' }}>
                <span style={{ ...styles.upgradeText, color: theme.textSecondary }}>
                  Upgrade to Basic tier or higher to add weekly events
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Special Events (One-Time) - Enhanced+ tiers */}
      <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <div style={styles.sectionHeader}>
          <CalendarIcon />
          <div style={styles.sectionTitleWrap}>
            <h3 style={{ ...styles.sectionTitle, color: theme.text }}>Special Events</h3>
            <span style={{ ...styles.tierLimit, color: theme.textMuted }}>
              {specialEventsThisYear} / {specialEventLimit} this year
            </span>
          </div>
          {canAddMoreSpecialEvents && specialEventLimit > 0 && (
            <button
              style={{ ...styles.addButton, backgroundColor: '#f59e0b' }}
              onClick={() => setShowAddSpecial(true)}
            >
              <PlusIcon />
              <span>Add</span>
            </button>
          )}
        </div>

        <p style={{ ...styles.blockedDesc, color: theme.textSecondary }}>
          One-time events like grand openings, special sales, or tournaments. Followers will be notified!
        </p>

        {specialEventsLoading ? (
          <div style={styles.loadingState}>
            <div style={{ ...styles.spinner, borderTopColor: '#f59e0b' }} />
          </div>
        ) : specialEventLimit === 0 ? (
          <div style={{ ...styles.upgradeBox, backgroundColor: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)' }}>
            <span style={{ ...styles.upgradeText, color: theme.textSecondary }}>
              Upgrade to Enhanced tier or higher to create special events
            </span>
          </div>
        ) : specialEvents.length === 0 ? (
          <div style={{ ...styles.emptyState, backgroundColor: theme.bg, borderColor: theme.border }}>
            <span style={{ color: theme.textMuted }}>No upcoming special events</span>
          </div>
        ) : (
          <div style={styles.eventSlots}>
            {specialEvents.map((event) => (
              <div key={event.id} style={{ ...styles.eventSlot, backgroundColor: theme.bg, borderColor: theme.border }}>
                <div style={styles.specialEventDate}>
                  <span style={{ ...styles.specialDateText, color: '#f59e0b' }}>{formatEventDate(event.event_date)}</span>
                </div>
                <div style={styles.eventInfo}>
                  <span style={{ ...styles.eventName, color: theme.text }}>{event.name}</span>
                  <span style={{ ...styles.eventTime, color: theme.textSecondary }}>
                    {formatEventTime(event.start_time)} - {formatEventTime(event.end_time)}
                  </span>
                  {event.description && (
                    <span style={{ ...styles.eventDesc, color: theme.textMuted }}>{event.description}</span>
                  )}
                </div>
                <button style={styles.removeButton} onClick={() => removeSpecialEvent(event.id)}>
                  <TrashIcon />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Operating Hours - Collapsible */}
      <div style={{ ...styles.section, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <button
          style={styles.collapsibleHeader}
          onClick={() => !isEditingHours && setHoursCollapsed(!hoursCollapsed)}
        >
          <ClockIcon />
          <h3 style={{ ...styles.sectionTitle, color: theme.text, flex: 1, textAlign: 'left' }}>Operating Hours</h3>
          {!isEditingHours && (
            <ChevronDownIcon style={{
              transform: hoursCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.2s',
              color: theme.textSecondary,
            }} />
          )}
        </button>

        {!hoursCollapsed && (
          <>
            <div style={{ ...styles.hoursActions, marginTop: 12 }}>
              {!isEditingHours ? (
                <button style={styles.editButton} onClick={startEditingHours}>
                  <EditIcon />
                  <span>Edit</span>
                </button>
              ) : (
                <div style={styles.headerActions}>
                  <button
                    style={styles.cancelButton}
                    onClick={cancelEditingHours}
                    disabled={hoursSaving}
                  >
                    Cancel
                  </button>
                  <button
                    style={styles.saveButton}
                    onClick={saveHours}
                    disabled={hoursSaving}
                  >
                    <SaveIcon />
                    <span>{hoursSaving ? 'Saving...' : 'Save'}</span>
                  </button>
                </div>
              )}
            </div>

            {!isEditingHours ? (
              // View Mode
              <div style={styles.hoursGrid}>
                {DAYS_ORDER.map(day => (
                  <div key={day} style={{ ...styles.hoursRow, borderColor: theme.border }}>
                    <span style={{ ...styles.dayLabel, color: theme.text }}>{day}</span>
                    <span style={{
                      ...styles.hoursDisplay,
                      color: hours[day]?.toLowerCase() === 'closed' ? theme.textMuted : theme.text
                    }}>
                      {hours[day] || 'Not set'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              // Edit Mode
              <div style={styles.hoursGrid}>
                {DAYS_ORDER.map(day => {
                  const dayData = editHours[day] || { closed: false, open: '', close: '' };
                  return (
                    <div key={day} style={{ ...styles.hoursEditRow, borderColor: theme.border }}>
                      <div style={styles.dayHeader}>
                        <span style={{ ...styles.dayLabel, color: theme.text }}>{day}</span>
                        <button
                          style={{
                            ...styles.closedToggle,
                            backgroundColor: dayData.closed ? '#ef4444' : 'rgba(0,0,0,0.1)',
                            color: dayData.closed ? '#fff' : theme.textSecondary,
                          }}
                          onClick={() => toggleDayClosed(day)}
                        >
                          {dayData.closed ? 'Closed' : 'Open'}
                        </button>
                      </div>
                      {!dayData.closed && (
                        <div style={styles.timeSelectors}>
                          <select
                            value={dayData.open || ''}
                            onChange={(e) => handleEditHoursChange(day, 'open', e.target.value)}
                            style={{
                              ...styles.timeSelect,
                              backgroundColor: theme.bg,
                              borderColor: theme.border,
                              color: theme.text,
                            }}
                          >
                            <option value="">Open</option>
                            {TIME_OPTIONS.map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <span style={{ color: theme.textSecondary }}>to</span>
                          <select
                            value={dayData.close || ''}
                            onChange={(e) => handleEditHoursChange(day, 'close', e.target.value)}
                            style={{
                              ...styles.timeSelect,
                              backgroundColor: theme.bg,
                              borderColor: theme.border,
                              color: theme.text,
                            }}
                          >
                            <option value="">Close</option>
                            {TIME_OPTIONS.map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Blocked Date Modal */}
      {showAddBlocked && (
        <>
          <div style={styles.modalOverlay} onClick={() => setShowAddBlocked(false)} />
          <div style={{ ...styles.modal, backgroundColor: theme.bgSheet }}>
            <div style={{ ...styles.modalHeader, borderColor: theme.border }}>
              <h3 style={{ ...styles.modalTitle, color: theme.text }}>Block a Date</h3>
              <button style={{ ...styles.modalClose, color: theme.textSecondary }} onClick={() => setShowAddBlocked(false)}>
                <XIcon />
              </button>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.formGroup}>
                <label style={{ ...styles.formLabel, color: theme.text }}>Date</label>
                <input
                  type="date"
                  value={newBlockedDate}
                  onChange={(e) => setNewBlockedDate(e.target.value)}
                  min={today}
                  style={{
                    ...styles.formInput,
                    backgroundColor: theme.bg,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{ ...styles.formLabel, color: theme.text }}>Reason (optional)</label>
                <input
                  type="text"
                  value={newBlockedReason}
                  onChange={(e) => setNewBlockedReason(e.target.value)}
                  placeholder="e.g. Holiday, Private Event"
                  style={{
                    ...styles.formInput,
                    backgroundColor: theme.bg,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                />
              </div>

              <button
                style={{
                  ...styles.submitButton,
                  opacity: !newBlockedDate ? 0.5 : 1,
                }}
                onClick={addBlockedDate}
                disabled={!newBlockedDate}
              >
                Block This Date
              </button>
            </div>
          </div>
        </>
      )}

      {/* Add Weekly Event Modal */}
      {showAddEvent && (
        <>
          <div style={styles.modalOverlay} onClick={() => setShowAddEvent(false)} />
          <div style={{ ...styles.modal, backgroundColor: theme.bgSheet }}>
            <div style={{ ...styles.modalHeader, borderColor: theme.border }}>
              <h3 style={{ ...styles.modalTitle, color: theme.text }}>Add Weekly Event</h3>
              <button style={{ ...styles.modalClose, color: theme.textSecondary }} onClick={() => setShowAddEvent(false)}>
                <XIcon />
              </button>
            </div>

            <div style={styles.modalContent}>
              {/* Event Type */}
              <div style={styles.formGroup}>
                <label style={{ ...styles.formLabel, color: theme.text }}>Event Type *</label>
                <div style={styles.eventTypeGrid}>
                  {EVENT_TYPES.map(type => (
                    <button
                      key={type.id}
                      style={{
                        ...styles.eventTypeBtn,
                        backgroundColor: newEventType === type.id ? '#8b5cf6' : theme.bg,
                        borderColor: newEventType === type.id ? '#8b5cf6' : theme.border,
                        color: newEventType === type.id ? '#fff' : theme.text,
                      }}
                      onClick={() => setNewEventType(type.id)}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Day of Week */}
              <div style={styles.formGroup}>
                <label style={{ ...styles.formLabel, color: theme.text }}>Day of Week *</label>
                <select
                  value={newEventDay}
                  onChange={(e) => setNewEventDay(e.target.value)}
                  style={{
                    ...styles.formInput,
                    backgroundColor: theme.bg,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                >
                  <option value="">Select a day</option>
                  {DAYS_ORDER.map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>

              {/* Time Row */}
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={{ ...styles.formLabel, color: theme.text }}>Start *</label>
                  <select
                    value={newEventStart}
                    onChange={(e) => setNewEventStart(e.target.value)}
                    style={{
                      ...styles.formInput,
                      backgroundColor: theme.bg,
                      borderColor: theme.border,
                      color: theme.text,
                    }}
                  >
                    <option value="">Start</option>
                    {TIME_OPTIONS.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={{ ...styles.formLabel, color: theme.text }}>End *</label>
                  <select
                    value={newEventEnd}
                    onChange={(e) => setNewEventEnd(e.target.value)}
                    style={{
                      ...styles.formInput,
                      backgroundColor: theme.bg,
                      borderColor: theme.border,
                      color: theme.text,
                    }}
                  >
                    <option value="">End</option>
                    {TIME_OPTIONS.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preview */}
              {newEventType && newEventDay && newEventStart && newEventEnd && (
                <div style={{ ...styles.eventPreview, backgroundColor: theme.bg, borderColor: theme.border }}>
                  <span style={{ ...styles.previewLabel, color: theme.textMuted }}>Preview:</span>
                  <span style={{ ...styles.previewName, color: theme.text }}>
                    {generateEventName(newEventType, newEventDay, newEventStart.includes('PM') && parseInt(newEventStart) >= 5 ? '17:00:00' : '10:00:00')}
                  </span>
                  <span style={{ ...styles.previewTime, color: theme.textSecondary }}>
                    {newEventDay}s, {newEventStart} - {newEventEnd}
                  </span>
                </div>
              )}

              <button
                style={{
                  ...styles.submitButton,
                  opacity: (!newEventType || !newEventDay || !newEventStart || !newEventEnd || eventSaving) ? 0.5 : 1,
                }}
                onClick={addWeeklyEvent}
                disabled={!newEventType || !newEventDay || !newEventStart || !newEventEnd || eventSaving}
              >
                {eventSaving ? 'Adding...' : 'Add Event'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Add Special Event Modal */}
      {showAddSpecial && (
        <>
          <div style={styles.modalOverlay} onClick={() => setShowAddSpecial(false)} />
          <div style={{ ...styles.modal, backgroundColor: theme.bgSheet }}>
            <div style={{ ...styles.modalHeader, borderColor: theme.border }}>
              <h3 style={{ ...styles.modalTitle, color: theme.text }}>Add Special Event</h3>
              <button style={{ ...styles.modalClose, color: theme.textSecondary }} onClick={() => setShowAddSpecial(false)}>
                <XIcon />
              </button>
            </div>

            <div style={styles.modalContent}>
              {/* Event Name */}
              <div style={styles.formGroup}>
                <label style={{ ...styles.formLabel, color: theme.text }}>Event Name *</label>
                <input
                  type="text"
                  value={newSpecialName}
                  onChange={(e) => setNewSpecialName(e.target.value)}
                  placeholder="e.g. Grand Opening, Regional Tournament"
                  style={{
                    ...styles.formInput,
                    backgroundColor: theme.bg,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                />
              </div>

              {/* Date */}
              <div style={styles.formGroup}>
                <label style={{ ...styles.formLabel, color: theme.text }}>Date *</label>
                <input
                  type="date"
                  value={newSpecialDate}
                  onChange={(e) => setNewSpecialDate(e.target.value)}
                  min={today}
                  style={{
                    ...styles.formInput,
                    backgroundColor: theme.bg,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                />
              </div>

              {/* Time Row */}
              <div style={styles.formRow}>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={{ ...styles.formLabel, color: theme.text }}>Start *</label>
                  <select
                    value={newSpecialStart}
                    onChange={(e) => setNewSpecialStart(e.target.value)}
                    style={{
                      ...styles.formInput,
                      backgroundColor: theme.bg,
                      borderColor: theme.border,
                      color: theme.text,
                    }}
                  >
                    <option value="">Start</option>
                    {TIME_OPTIONS.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={{ ...styles.formLabel, color: theme.text }}>End *</label>
                  <select
                    value={newSpecialEnd}
                    onChange={(e) => setNewSpecialEnd(e.target.value)}
                    style={{
                      ...styles.formInput,
                      backgroundColor: theme.bg,
                      borderColor: theme.border,
                      color: theme.text,
                    }}
                  >
                    <option value="">End</option>
                    {TIME_OPTIONS.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div style={styles.formGroup}>
                <label style={{ ...styles.formLabel, color: theme.text }}>Description (optional)</label>
                <input
                  type="text"
                  value={newSpecialDesc}
                  onChange={(e) => setNewSpecialDesc(e.target.value)}
                  placeholder="Brief description of the event"
                  style={{
                    ...styles.formInput,
                    backgroundColor: theme.bg,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                />
              </div>

              {/* Notification Note */}
              <div style={{ ...styles.notificationNote, backgroundColor: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)' }}>
                <span style={{ color: '#f59e0b', fontSize: 13 }}>
                  All your followers will be notified about this event!
                </span>
              </div>

              <button
                style={{
                  ...styles.submitButton,
                  backgroundColor: '#f59e0b',
                  opacity: (!newSpecialName || !newSpecialDate || !newSpecialStart || !newSpecialEnd || specialSaving) ? 0.5 : 1,
                }}
                onClick={addSpecialEvent}
                disabled={!newSpecialName || !newSpecialDate || !newSpecialStart || !newSpecialEnd || specialSaving}
              >
                {specialSaving ? 'Creating...' : 'Create Event'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: 16,
    paddingBottom: 120,
  },
  section: {
    padding: 16,
    borderRadius: 16,
    border: '1px solid',
    marginBottom: 16,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
  },
  sectionTitleWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    margin: 0,
    flex: 1,
  },
  tierLimit: {
    fontSize: 12,
  },
  collapsibleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: 0,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  hoursActions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  upgradeBox: {
    padding: 16,
    borderRadius: 10,
    border: '1px solid',
    textAlign: 'center',
  },
  upgradeText: {
    fontSize: 14,
  },
  eventSlots: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  eventSlot: {
    display: 'flex',
    alignItems: 'center',
    padding: 12,
    borderRadius: 10,
    border: '1px solid',
    gap: 12,
  },
  slotNumber: {
    flexShrink: 0,
  },
  slotBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
  },
  emptySlot: {
    borderStyle: 'dashed',
  },
  emptySlotText: {
    fontSize: 14,
  },
  inactiveSlot: {
    opacity: 0.5,
  },
  lockedSlot: {
    opacity: 0.4,
  },
  lockedSlotText: {
    fontSize: 13,
    fontStyle: 'italic',
  },
  separatorLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '8px 0',
    position: 'relative',
  },
  separatorText: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    padding: '0 12px',
  },
  eventInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  eventName: {
    fontSize: 15,
    fontWeight: 600,
  },
  eventTime: {
    fontSize: 13,
  },
  eventTypeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
  },
  eventTypeBtn: {
    padding: '12px 8px',
    borderRadius: 10,
    border: '1px solid',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'center',
  },
  eventPreview: {
    padding: 12,
    borderRadius: 10,
    border: '1px solid',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  previewLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  previewName: {
    fontSize: 16,
    fontWeight: 600,
  },
  previewTime: {
    fontSize: 13,
  },
  formRow: {
    display: 'flex',
    gap: 12,
  },
  readOnlyCard: {
    padding: 12,
    borderRadius: 10,
    border: '1px solid',
    marginBottom: 12,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
  },
  infoLabel: {
    fontSize: 13,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: 500,
  },
  contactNote: {
    display: 'flex',
    gap: 12,
    padding: 14,
    borderRadius: 10,
    border: '1px solid',
  },
  contactNoteText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  contactNoteTitle: {
    fontSize: 13,
    fontWeight: 600,
  },
  contactNoteDesc: {
    fontSize: 12,
    lineHeight: 1.4,
  },
  hoursGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  hoursRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingBottom: 8,
    borderBottom: '1px solid',
  },
  hoursEditRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingBottom: 12,
    borderBottom: '1px solid',
  },
  dayHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dayLabel: {
    fontSize: 14,
    fontWeight: 500,
  },
  hoursDisplay: {
    fontSize: 14,
  },
  timeSelectors: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  timeSelect: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid',
    fontSize: 14,
    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    cursor: 'pointer',
  },
  closedToggle: {
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  headerActions: {
    display: 'flex',
    gap: 8,
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    color: '#8b5cf6',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  cancelButton: {
    padding: '8px 14px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    backgroundColor: '#22c55e',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  blockedDesc: {
    fontSize: 13,
    lineHeight: 1.5,
    margin: '0 0 16px 0',
  },
  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    padding: 24,
  },
  spinner: {
    width: 24,
    height: 24,
    border: '3px solid rgba(0,0,0,0.1)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  emptyState: {
    padding: 24,
    borderRadius: 10,
    border: '1px dashed',
    textAlign: 'center',
  },
  blockedList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  blockedCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 14,
    borderRadius: 10,
    border: '1px solid',
  },
  blockedInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  blockedDate: {
    fontSize: 14,
    fontWeight: 600,
  },
  blockedReason: {
    fontSize: 12,
  },
  removeButton: {
    padding: 8,
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  },
  modal: {
    position: 'fixed',
    bottom: 'calc(90px + env(safe-area-inset-bottom))',
    left: 12,
    right: 12,
    maxHeight: '70vh',
    borderRadius: 16,
    padding: 16,
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: '1px solid',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  modalClose: {
    padding: 8,
    margin: -8,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: 500,
  },
  formInput: {
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid',
    fontSize: 15,
    outline: 'none',
  },
  submitButton: {
    padding: '14px 20px',
    backgroundColor: '#8b5cf6',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
  specialEventDate: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 70,
    padding: '8px 4px',
  },
  specialDateText: {
    fontSize: 12,
    fontWeight: 700,
    textAlign: 'center',
  },
  eventDesc: {
    fontSize: 12,
    lineHeight: 1.3,
    marginTop: 2,
  },
  notificationNote: {
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid',
    textAlign: 'center',
  },
};

export default LocationMyShop;
